import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@mui/material";
import { isEqual } from "lodash";
import { Stack, Columns } from "components/layout/reusableLayouts";
import { FormikProps } from "formik";
import { useMask } from "@react-input/mask";

// Local components
import PlanPickerDrawer from "components/platform/accounts/PlanPickerDrawer";

// Local utilities and validations
import { AccountDetailsDrawerType } from "enums/AccountDetailsDrawerType";
import Role from "enums/Role";
import { useAssignableRoles } from "hooks/useAssignableRoles";
import { useCompanyAppFeatureKeys } from "hooks/useCompanyAppFeatureKeys";
import { FormikAddAccount } from "hooks/useAccountData";
import { useCheckUserRole } from "hooks/useCheckUserRole";
import usePlatformIntegrations from "hooks/usePlatformIntegrations";
import PlatformIntegrationCategory from "enums/PlatformIntegrationCategory";
import ApplicationType from "enums/ApplicationType";
import CardHolderType from "enums/CardHolderType";
import { CARD_HOLDER_LABEL_KEY } from "utils/platformIntegrations/integrationLabels";
import CardProgramStatus from "enums/CardProgramStatus";
import NewAccountReviewDrawer from "components/platform/accounts/NewAccountReviewDrawer";
import { useMutation, useQuery } from "@apollo/client";
import { GET_MY_PLATFORM } from "graphql/platform";
import { Drawer, Select, TextField } from "@outpave/ds";
import PlatformConfigureAddressDrawer from "../migrate/PlatformConfigureAddressDrawer";
import AddressField from "components/common/address/AddressField";
import { useDrawerRecord } from "hooks/useDrawerRecord";
import { useUnsavedChangesGuard } from "hooks/useUnsavedChangesGuard";
import { BUSINESS_CARD_NAME_MAX_LENGTH } from "validations/companySettings";

type PlanSelection = {
  appPlanIds: string[];
  addOnPlanIds: string[];
};

const sameIds = (a: string[] = [], b: string[] = []) =>
  isEqual([...a].sort(), [...b].sort());

interface EditAccountDrawerProps {
  open: boolean;
  formik: FormikProps<FormikAddAccount>;
  handleClose: (
    refresh?: boolean,
    openAccountDetailsWithId?: string,
    newBusinessname?: string,
  ) => void;
  drawerType: AccountDetailsDrawerType;
  accountId?: string;
  companyId?: string;
  /** Account create/update in flight. Gates the overlay and every close path. */
  savingAccount?: boolean;
  /** Fires once the drawer has slid out. */
  onAfterClose?: () => void;
}

const EditAccountDrawer: React.FC<EditAccountDrawerProps> = ({
  open,
  handleClose,
  drawerType,
  formik,
  accountId,
  companyId,
  savingAccount = false,
  onAfterClose,
}) => {
  const { t } = useTranslation();

  // The address form is built from its initial values on mount, so each open mounts it afresh.
  const addressDrawer = useDrawerRecord<true>();
  const [openDetailsDrawer, setOpenDetailsDrawer] = useState(false);
  const [openPlanPicker, setOpenPlanPicker] = useState(false);
  // The plans, issuer and cardholders start from defaults the drawer sets itself, so only a pick of one counts.
  const [choicesChanged, setChoicesChanged] = useState(false);

  const isNewAccount = drawerType === AccountDetailsDrawerType.NewAccount;
  // An existing account's plans live on its Plans tab; this drawer picks them only for a new one.
  const canManagePlans = useCheckUserRole(Role.PlatformOwner) && isNewAccount;

  // Queries wait for the first open and then stay live, so no field drops out while the drawer slides away.
  const [hasOpened, setHasOpened] = useState(open);
  if (open && !hasOpened) setHasOpened(true);

  // A new account starts from the platform's default plans.
  const { data: platformData } = useQuery(GET_MY_PLATFORM, {
    skip: !hasOpened || !canManagePlans,
    fetchPolicy: "cache-and-network",
  });

  useEffect(() => {
    if (!open || !canManagePlans) return;
    if (platformData?.myPlatform) {
      formik.setFieldValue(
        "appPlanIds",
        platformData.myPlatform.defaultAppPlanIds || [],
      );
      formik.setFieldValue(
        "addOnPlanIds",
        platformData.myPlatform.defaultAddOnPlanIds || [],
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, canManagePlans, platformData]);

  const { integrations: platformIntegrations } = usePlatformIntegrations({
    skip: !hasOpened || !isNewAccount || !canManagePlans,
    category: PlatformIntegrationCategory.CARD_ISSUING,
  });
  // A live integration with at least one card program switched on, as the server checks.
  const offeredIssuers = useMemo(
    () =>
      platformIntegrations.filter(
        (integration) =>
          integration.status === CardProgramStatus.ACTIVE &&
          integration.cardProducts.some((product) => product.enabled),
      ),
    [platformIntegrations],
  );
  const choosesIssuer = isNewAccount && offeredIssuers.length > 1;
  // The company picks who holds its cards from what its issuer's programs offer.
  const offeredCardHolderTypes = useMemo(() => {
    const issuer =
      offeredIssuers.find(
        (integration) => integration.id === formik.values.platformIntegrationId,
      ) ?? (offeredIssuers.length === 1 ? offeredIssuers[0] : null);
    const enabled = (issuer?.cardProducts ?? []).filter(
      (product) => product.enabled,
    );
    // The server opens a debit program when one is offered, as it does for a company created here.
    const accountType = enabled.some(
      (product) => product.subAccountType === ApplicationType.DEBIT,
    )
      ? ApplicationType.DEBIT
      : enabled[0]?.subAccountType;
    const types = enabled
      .filter(
        (product) =>
          !product.subAccountType || product.subAccountType === accountType,
      )
      .flatMap((product) => product.cardHolderTypes);
    return Object.values(CardHolderType).filter((type) => types.includes(type));
  }, [offeredIssuers, formik.values.platformIntegrationId]);
  const choosesCardHolderType =
    isNewAccount && offeredCardHolderTypes.length > 1;

  useEffect(() => {
    if (!open || !isNewAccount) return;
    const current = formik.values.cardHolderType;
    if (
      choosesCardHolderType &&
      current &&
      offeredCardHolderTypes.includes(current)
    )
      return;
    formik.setFieldValue(
      "cardHolderType",
      choosesCardHolderType ? CardHolderType.AUTHORIZED_USER : undefined,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isNewAccount, choosesCardHolderType, offeredCardHolderTypes]);

  useEffect(() => {
    if (!open || !choosesIssuer || formik.values.platformIntegrationId) return;
    const fallback =
      offeredIssuers.find((integration) => integration.isDefault) ??
      offeredIssuers[0];
    formik.setFieldValue("platformIntegrationId", fallback.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, choosesIssuer, offeredIssuers]);

  const handlePlanSave = (next: PlanSelection) => {
    if (
      !sameIds(next.appPlanIds, formik.values.appPlanIds) ||
      !sameIds(next.addOnPlanIds, formik.values.addOnPlanIds)
    )
      setChoicesChanged(true);
    formik.setFieldValue("appPlanIds", next.appPlanIds);
    formik.setFieldValue("addOnPlanIds", next.addOnPlanIds);
  };

  const targetAppFeatureKeys = useCompanyAppFeatureKeys(companyId, {
    skip: !hasOpened,
  });
  const { options: roleOptions, platformAppMissing } = useAssignableRoles(
    [Role.Admin, Role.PlatformAdmin, Role.PlatformOwner],
    formik.values.user?.role as Role,
    companyId,
    targetAppFeatureKeys,
  );

  // Gates the overlay AND every dismissal path, so the two can't drift.
  // `savingAccount` is the create/update the parent runs. The parent's own
  // LoadingWithoutModal sits at zIndex.drawer + 1, below this drawer's modal,
  // so it never covered it.
  const busy = savingAccount;

  const hasChanges = isNewAccount
    ? choicesChanged ||
      formik.values.account.businessLegalName !==
        formik.initialValues.account.businessLegalName ||
      !isEqual(formik.values.user, formik.initialValues.user)
    : formik.dirty;

  const { requestLeave, dialog } = useUnsavedChangesGuard(hasChanges, () =>
    handleClose(false),
  );

  // The X, backdrop, Escape and Exit leave here, from either step; a save closes on its own.
  const requestClose = () => {
    if (busy) return;
    requestLeave();
  };

  const handleNextStep = () => {
    if (isNewAccount) {
      setOpenDetailsDrawer(true);
      return;
    }
    // An edit stays open under its overlay until the save lands, so a failed one keeps the edits.
    formik.submitForm();
  };

  const handleBlurEmail = (e: { target: { name: string } }) => {
    formik.handleBlur(e);
  };

  // Required by both validation schemas, so it shows its error like the fields beside it.
  const accountNameError = formik.touched.account?.businessLegalName
    ? (formik.errors.account?.businessLegalName as string | undefined)
    : undefined;
  // Shown untouched: a stored name can already break the rule and disable Save.
  const cardNameError = formik.errors.account?.businessCardName as
    string | undefined;

  useEffect(() => {
    setTimeout(() => {
      formik.validateForm();
    }, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values]);

  const phoneMaskRef = useMask({
    mask: "___-___-____",
    replacement: { _: /\d/ },
  });

  return (
    <>
      <Drawer
        open={open}
        onAfterClose={onAfterClose}
        loading={busy}
        title={
          drawerType === AccountDetailsDrawerType.NewAccount
            ? t("Add Account")
            : t("Edit Account")
        }
        footer={
          <>
            <Button
              variant="primary-outlined"
              disabled={busy}
              onClick={requestClose}
            >
              {t("Exit")}
            </Button>

            {canManagePlans && (
              <Button
                variant="primary-outlined"
                onClick={() => setOpenPlanPicker(true)}
                disabled={busy}
              >
                {t("Manage Plans")}
              </Button>
            )}

            <Button
              variant="primary-filled"
              onClick={() => {
                handleNextStep();
              }}
              disabled={
                busy ||
                Object.keys(formik.errors).length > 0 ||
                !formik.isValid ||
                !formik.dirty
              }
            >
              {drawerType === AccountDetailsDrawerType.NewAccount
                ? t("Next")
                : t("Save")}
            </Button>
          </>
        }
        onClose={requestClose}
      >
        <form onSubmit={formik.handleSubmit}>
          {drawerType === AccountDetailsDrawerType.NewAccount ? (
            <Stack>
              <TextField
                label={t("Account Name")}
                required
                fullWidth
                placeholder={t("Account Name")}
                name="account.businessLegalName"
                value={formik.values.account.businessLegalName}
                onChange={formik.handleChange}
                onBlur={handleBlurEmail}
                errorText={accountNameError}
              />

              {choosesIssuer && (
                <Select
                  label={t("Card issuer")}
                  required
                  fullWidth
                  options={offeredIssuers.map((integration) => ({
                    id: integration.id,
                    label: integration.name,
                    caption: integration.cardProducts
                      .filter((product) => product.enabled)
                      .map((product) => product.displayName)
                      .filter(Boolean)
                      .join(", "),
                  }))}
                  value={formik.values.platformIntegrationId ?? null}
                  onChange={(id) => {
                    setChoicesChanged(true);
                    formik.setFieldValue("platformIntegrationId", id);
                  }}
                  helperText={t("NewAccountIssuerHelp")}
                />
              )}

              {choosesCardHolderType && (
                <Select
                  label={t("Cardholders")}
                  required
                  fullWidth
                  clearable={false}
                  options={offeredCardHolderTypes.map((type) => ({
                    id: type,
                    label: t(CARD_HOLDER_LABEL_KEY[type]),
                  }))}
                  value={formik.values.cardHolderType ?? null}
                  onChange={(id) => {
                    setChoicesChanged(true);
                    formik.setFieldValue("cardHolderType", id);
                  }}
                  helperText={t("NewAccountCardHoldersHelp")}
                />
              )}

              <Columns count={2}>
                <TextField
                  label={t("First Name")}
                  required
                  fullWidth
                  placeholder={t("First Name")}
                  name="user.firstName"
                  value={formik.values.user.firstName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  errorText={
                    formik.touched.user?.firstName
                      ? formik.errors.user?.firstName
                      : undefined
                  }
                />
                <TextField
                  label={t("Last Name")}
                  required
                  fullWidth
                  placeholder={t("Last Name")}
                  name="user.lastName"
                  value={formik.values.user.lastName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  errorText={
                    formik.touched.user?.lastName
                      ? formik.errors.user?.lastName
                      : undefined
                  }
                />

                <TextField
                  label={t("Phone")}
                  required
                  fullWidth
                  ref={phoneMaskRef}
                  name="user.phone"
                  value={formik.values.user?.phone}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  errorText={
                    formik.touched.user?.phone
                      ? formik.errors.user?.phone
                      : undefined
                  }
                />

                <Select
                  label={t("Role")}
                  required
                  fullWidth
                  options={roleOptions.map(({ role, disabled }) => ({
                    id: role,
                    label: t(role),
                    disabled,
                  }))}
                  value={formik.values.user?.role ?? null}
                  onChange={(id) => formik.setFieldValue("user.role", id)}
                  helperText={
                    platformAppMissing
                      ? t("PlatformRolesNeedPlatformApp")
                      : undefined
                  }
                  errorText={
                    formik.touched.user?.role
                      ? (formik.errors.user?.role as string | undefined)
                      : undefined
                  }
                />
              </Columns>

              <TextField
                label={t("Email")}
                required
                fullWidth
                name="user.email"
                value={formik.values.user.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                errorText={
                  formik.touched.user?.email
                    ? formik.errors.user?.email
                    : undefined
                }
              />

              <AddressField
                label={t("Address")}
                required
                placeholder={t("Address")}
                name="user.address"
                address={formik.values.user.address}
                onOpen={() => addressDrawer.show(true)}
                errorText={
                  formik.touched.user?.address
                    ? formik.errors.user?.address?.addressName
                    : undefined
                }
              />
            </Stack>
          ) : (
            <Stack>
              <TextField
                label={t("Legal Name")}
                required
                fullWidth
                placeholder={t("Legal Name")}
                name="account.businessLegalName"
                value={formik.values.account.businessLegalName}
                onChange={formik.handleChange}
                onBlur={handleBlurEmail}
                errorText={accountNameError}
              />
              <TextField
                label={t("Doing Business As (DBA)")}
                fullWidth
                placeholder={t("Doing Business As (DBA)")}
                name="account.businessDBA"
                value={formik.values.account.businessDBA}
                onChange={formik.handleChange}
                onBlur={handleBlurEmail}
              />
              <TextField
                label={t("Business Card Name")}
                fullWidth
                placeholder={t("Business Card Name")}
                name="account.businessCardName"
                value={formik.values.account.businessCardName}
                onChange={formik.handleChange}
                onBlur={handleBlurEmail}
                errorText={cardNameError ? t(cardNameError) : undefined}
                inputProps={{ maxLength: BUSINESS_CARD_NAME_MAX_LENGTH }}
              />
              <TextField
                label={t("Description")}
                fullWidth
                placeholder={t("Description")}
                name="account.description"
                value={formik.values.account.description}
                onChange={formik.handleChange}
                onBlur={handleBlurEmail}
              />
              <TextField
                label={t("Email")}
                fullWidth
                placeholder={t("Email")}
                name="account.businessEmail"
                value={formik.values.account.businessEmail}
                onChange={formik.handleChange}
                onBlur={handleBlurEmail}
              />
              <TextField
                label={t("Phone")}
                fullWidth
                placeholder={t("Phone")}
                name="account.phoneNumber"
                value={formik.values.account.phoneNumber}
                onChange={formik.handleChange}
                onBlur={handleBlurEmail}
              />
            </Stack>
          )}
        </form>
      </Drawer>

      {canManagePlans && (
        <PlanPickerDrawer
          open={openPlanPicker}
          onClose={() => setOpenPlanPicker(false)}
          appPlanIds={formik.values.appPlanIds || []}
          addOnPlanIds={formik.values.addOnPlanIds || []}
          onSave={handlePlanSave}
          companyName={formik.values.account.businessLegalName}
        />
      )}

      {/* Stays mounted, so Back slides it out: it only reads the parent's form. */}
      {isNewAccount && (
        <NewAccountReviewDrawer
          formik={formik}
          open={open && openDetailsDrawer}
          handleCloseDrawer={requestClose}
          onBack={() => setOpenDetailsDrawer(false)}
          savingAccount={savingAccount}
        />
      )}

      {addressDrawer.record && (
        <PlatformConfigureAddressDrawer
          open={addressDrawer.open}
          drawerTitle={[t("User Address")]}
          initialValues={formik.values.user.address}
          onClose={addressDrawer.close}
          onAfterClose={addressDrawer.afterClose}
          onSuccess={(address: any) => {
            formik.setFieldValue("user.address", address);
            addressDrawer.close();
          }}
          header={t("User Address")}
          subheader=""
          readOnly={false}
          customAddressName={false}
        />
      )}

      {dialog}
    </>
  );
};

export default EditAccountDrawer;
